import { Link } from 'react-router-dom';
import { useLang, useSeo } from '@/i18n/LanguageContext';
import PageHero from '@/components/PageHero';
import CtaBand from '@/components/CtaBand';
import SectionHead from '@/components/SectionHead';
import Icon from '@/components/Icon';
import { Reveal } from '@/components/Reveal';
import { Info, Wrench, Satellite, Headset, Smartphone } from 'lucide-react';

const STEP_ICONS = [Wrench, Satellite, Headset, Smartphone];

export default function Products() {
  const { content, route } = useLang();
  useSeo(`${content.products.title} | Rodotrack`, content.products.subtitle);
  const c = content.products;

  return (
    <>
      <PageHero badge={c.eyebrow} title={c.title} subtitle={c.subtitle} />
{c.heroImg && (
        <div className="container-x mt-8 sm:hidden">
          <img src={c.heroImg} alt={c.title} loading="lazy" className="aspect-[4/3] w-full rounded-2xl object-cover" />
        </div>
      )}
      <section className="py-16 lg:py-20" data-testid="products-grid">
        <div className="container-x grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {c.items.map((p, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <article data-testid={`products-item-${i}`} className="flex h-full flex-col rounded-2xl border border-brand-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-blue/50 hover:shadow-[0_24px_48px_-24px_rgba(30,159,224,0.35)]">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-ice text-brand-blue">
                  <Icon name={p.icon} className="h-6 w-6" />
                </span>
                <h2 className="mt-5 font-display text-lg font-bold text-brand-navy">{p.name}</h2>
                <p className="mt-2 text-sm leading-relaxed text-brand-muted">{p.desc}</p>
                <ul className="mt-4 flex-1 space-y-2">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-[13px] text-brand-navy/80">
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-ice text-[9px] font-bold text-brand-blue">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                {p.img && (
                  <img
                    src={p.img}
                    alt={p.name}
                    loading="lazy"
                    className="mt-5 block aspect-[4/3] w-full rounded-xl object-cover sm:hidden"
                  />
                )}
                <Link to={route('contact')} data-testid={`products-cta-${i}`} className="mt-6 inline-flex items-center justify-center gap-1.5 rounded-full border border-brand-blue px-5 py-2.5 text-[13px] font-semibold text-brand-blue transition hover:bg-brand-blue hover:text-white">
                  {c.cta}
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-16 lg:pb-20" data-testid="products-accessories">
        <div className="container-x grid gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="h-full rounded-2xl border border-brand-line bg-brand-mist p-8">
              <h2 className="font-display text-2xl font-bold text-brand-navy">{c.accessories.title}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-brand-muted">{c.accessories.desc}</p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {c.accessories.items.map((item, i) => (
                  <span key={i} className="rounded-full border border-brand-line bg-white px-4 py-2 text-[13px] font-medium text-brand-navy">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5">
            <div data-testid="products-dashcam" className="grain relative h-full overflow-hidden rounded-2xl bg-brand-deep p-8">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-blue/25 blur-3xl" />
              <span className="relative inline-flex rounded-full bg-white/10 px-3 py-1 font-mono text-[11px] font-bold tracking-wider text-brand-cyan">Mov.IA</span>
              <h2 className="relative mt-4 font-display text-2xl font-bold text-white">{c.dashcam.name}</h2>
              <p className="relative mt-3 text-[14px] leading-relaxed text-white/70">{c.dashcam.desc}</p>
              <ul className="relative mt-5 space-y-2">
                {c.dashcam.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-[13px] text-white/85">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-blue/25 text-[9px] font-bold text-brand-cyan">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <div className="container-x mt-8">
          <Reveal>
            <p data-testid="products-support-phrase" className="flex items-center gap-2 text-center font-display text-xl font-bold text-brand-navy sm:text-2xl">
              <Info className="h-5 w-5 shrink-0 text-brand-blue" />
              {c.support}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-mist py-16 lg:py-24" data-testid="products-how">
        <div className="container-x grid items-start gap-12 lg:grid-cols-2">
          <div className="space-y-4">
            <SectionHead eyebrow={c.how.eyebrow} title={c.how.title} subtitle={c.how.subtitle} />
            <div className="space-y-4">
              {c.how.steps.map((s, i) => {
                const I = STEP_ICONS[i] || Satellite;
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    <div data-testid={`how-step-${i}`} className="group flex gap-5 rounded-2xl border border-brand-line bg-white p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-blue/50">
                      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-ice font-mono text-lg font-bold text-brand-blue transition-colors group-hover:bg-brand-blue group-hover:text-white">
                        {i + 1}
                      </span>
                      <div>
                        <h3 className="flex items-center gap-2 font-display text-lg font-bold text-brand-navy">
                          {s.title}
                          <I className="h-4 w-4 text-brand-cyan" />
                        </h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-brand-muted">{s.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
          <Reveal delay={0.1}>
            <div data-testid="how-image" className="overflow-hidden rounded-3xl">
              <img src={c.how.img} alt={c.how.title} loading="lazy" className="h-full w-full object-cover" style={{ minHeight: 420 }} />
            </div>
          </Reveal>
        </div>
      </section>

      <div className="-mt-10"><CtaBand /></div>
    </>
  );
   
}